import React from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { getPersonasForTenant } from "@/lib/mockData";
import { useTenantData } from "@/contexts/TenantModeContext";
import { cn } from "@/lib/utils";

/**
 * Demo Mode role switcher — visible only when in Demo Mode.
 * Swaps the active persona used by the role-based dashboard dispatcher.
 * Personas listed are scoped to the current tenant's org chart.
 */
export default function RoleSwitcher({ open, onToggle, currentId, onSwitch }) {
  const { tenant } = useTenantData();
  const personas = getPersonasForTenant(tenant?.id);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 px-2 h-9 rounded-md hover:bg-inset transition-colors text-meta text-ink-secondary"
        data-testid="header-role-switcher-trigger"
        title="Demo mode — switch persona"
      >
        <Sparkles size={12} className="text-gold-600" />
        <span className="hidden md:inline">Role</span>
        <ChevronDown size={12} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[280px] rounded-md border border-border-subtle bg-raised shadow-e3 anim-fade-up z-50"
          data-testid="header-role-switcher-menu"
        >
          <div className="px-3 py-2 border-b border-border-subtle">
            <p className="eyebrow">Demo Mode · {tenant?.name || "personas"}</p>
            <p className="text-meta text-ink-secondary mt-0.5">
              Switch persona to see role-tuned views.
            </p>
          </div>
          <ul className="max-h-[320px] overflow-y-auto py-1">
            {personas.map((p) => (
              <PersonaRow
                key={p.id}
                persona={p}
                active={currentId === p.id}
                onSelect={onSwitch}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PersonaRow({ persona, active, onSelect }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(persona.id)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-inset transition-colors",
          active && "bg-tralenex-50",
        )}
        data-testid={`role-switcher-${persona.id}`}
      >
        <span
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold shrink-0",
            active ? "bg-tralenex-600 text-white" : "bg-inset text-ink-primary",
          )}
        >
          {persona.initials}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-ink-primary truncate">
            {persona.name}
          </span>
          <span className="block text-meta text-ink-secondary truncate">
            {persona.role}
          </span>
        </span>
      </button>
    </li>
  );
}
